import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { theme } from 'app/theme'
import { rgba } from 'emotion-rgba'
import { uniqBy } from 'lodash'
import React, { memo, useState } from 'react'
import { ArrowLeft, ArrowRight } from 'react-feather'
import Fade from 'react-reveal/Fade'
import Slider from 'react-slick'

export interface Props {
  images?: ImageProps[]
  title?: string
  titleBackground?: string
  variant?: Variant
}

function PrevArrow(props: any) {
  return (
    <Arrow className="slider-prev" dial={5} onClick={props.onClick}>
      <ArrowLeft />
    </Arrow>
  )
}

function NextArrow(props: any) {
  return (
    <Arrow className="slider-next" dial={5} onClick={props.onClick}>
      <ArrowRight />
    </Arrow>
  )
}

export const GalleryImages = memo(function GalleryImages({
  images,
  title,
  titleBackground,
  variant = 'default',
}: Props) {
  const [currentSlide, setCurrentSlide] = useState(1)

  const settings = {
    centerMode: true,
    infinite: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    centerPadding: '17%',
    beforeChange: (current: number, next: number) => {
      setCurrentSlide(next + 1)
    },
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          centerPadding: '5%',
        },
      },
    ],
  }

  if (!images) {
    return null
  }

  return (
    <Container variant={variant}>
      {title ? (
        <>
          <Fade bottom distance="3.75rem">
            <Title
              style={
                variant === 'clip' || variant === 'clipLight'
                  ? { backgroundImage: `url(${titleBackground})` }
                  : undefined
              }
              variant={variant}
            >
              {title}
            </Title>
          </Fade>
          <Fade bottom distance="3.75rem">
            <Counter>{`${currentSlide}/${images.length}`}</Counter>
          </Fade>
        </>
      ) : null}
      <Fade bottom distance="3.75rem">
        <Slider lazyLoad="progressive" {...settings}>
          {uniqBy(images, 'src').map((item, index) => (
            <Image key={index} {...item} />
          ))}
        </Slider>
      </Fade>
    </Container>
  )
})

const Container = styled.section<ContainerProps>`
  background: ${({ theme }) => theme.colors.variants.neutralDark1};
  padding: 8.75rem 0 13.875rem;
  text-align: center;

  ${({ theme, variant }) => {
    switch (variant) {
      case 'light':
        return css`
          background: ${theme.colors.variants.primaryLight1};

          .slick-slider {
            .slick-slide {
              &:before {
                background: ${theme.colors.variants.primaryLight1} !important;
              }
            }
          }
        `
      case 'clipLight':
        return css`
          background: ${theme.colors.variants.primaryLight1};

          .slick-slider {
            .slick-slide {
              &:before {
                background: ${theme.colors.variants.primaryLight1} !important;
              }
            }
          }
        `
    }
  }}

  .slick-slider {
    .slick-track {
      display: flex;
      align-items: center;
      flex-wrap: nowrap;
      justify-content: center;
      transition: transform 2s;
    }
    .slick-slide {
      height: 47vh;
      margin: 14vh 2.25rem;
      position: relative;
      transition: 0.3s;
      &:before {
        content: '';
        width: 100%;
        height: 100%;
        background: ${({ theme }) => theme.colors.variants.neutralDark1};
        backface-visibility: hidden;
        opacity: 0.7;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 2;
        transition: 0.6s;
      }
      &.slick-current,
      &.slick-center {
        height: 75vh;
        margin: 0 2.25rem;
        &:before {
          opacity: 0;
        }
      }
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }

  @media (max-width: 1023px) {
    padding: 3.75rem 0 7.5rem;

    .slick-slider {
      .slick-slide {
        height: 23vh;
        margin: 7vh 0.375rem;
        &.slick-current,
        &.slick-center {
          height: 30vh;
          margin: 0 0.375rem;
        }
      }
    }
  }

  @media (max-width: 767px) {
    padding-bottom: 5.625rem;
  }
`

const Title = styled.h2<ContainerProps>`
  max-width: 800px;
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2.5rem;
  font-weight: 400;
  line-height: 3rem;
  margin: 0 auto 4.375rem;

  @media (max-width: 1023px) {
    font-size: 1.875rem;
    line-height: 2.25rem;
    margin-bottom: 1.875rem;
    padding: 0 8.454vw;
  }

  ${({ variant }) => {
    switch (variant) {
      case 'clip':
        return css`
          background-size: cover;
          background-clip: text;
          -webkit-text-fill-color: transparent;

          @media (max-width: 1023px) {
            background: none !important;
            -webkit-text-fill-color: ${theme.colors.variants.neutralLight4};
          }
        `
      case 'clipLight':
        return css`
          background-size: cover;
          background-clip: text;
          -webkit-text-fill-color: transparent;

          @media (max-width: 1023px) {
            background: none !important;
            -webkit-text-fill-color: ${theme.colors.variants.neutralLight4};
          }
        `
    }
  }}

  // safari browser rules
  @media not all and (min-resolution: 0.001dpcm) {
    @supports (-webkit-appearance: none) {
      -webkit-text-fill-color: ${({ theme }) =>
        theme.colors.variants.primaryDark1};
    }
  }
`

const Counter = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.375rem;
  line-height: 1.875rem;
  opacity: 0.24;
  margin-bottom: 2.125rem;
`

const Arrow = styled(FlexBox)`
  width: 5.25rem;
  height: 5.25rem;
  border: 1px solid ${rgba(theme.colors.variants.neutralLight4, 0.5)};
  border-radius: 50%;
  cursor: pointer;
  position: absolute;
  top: 50%;
  z-index: 3;
  transform: translateY(-50%);
  transition: 0.3s ease-in-out;
  &:hover {
    background: ${rgba(theme.colors.variants.neutralLight4, 0.5)};
    border-color: transparent;
  }
  &.slider-prev {
    left: calc(17% - 4.5625rem);
  }
  &.slider-next {
    right: calc(17% - 4.5625rem);
  }
  svg {
    width: auto;
    height: 2.5rem;
    stroke: ${({ theme }) => theme.colors.variants.neutralLight4};
    stroke-width: 1;
  }

  @media (max-width: 1023px) {
    display: none;
  }
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'light' | 'clip' | 'clipLight'
